import type {
  ApprovalStep,
  ApprovalStepDraft,
  ApprovalStepInput,
} from 'ui-product-approvals-common/types/ApprovalData';
import type {
  Actor,
  ActorType,
} from 'ui-product-approvals-common/types/ApprovalActor';
import {
  AgentTurnType,
  ApprovalStepStatus,
} from 'ui-product-approvals-common/types/ApprovalData';
import { ApprovalRules } from 'ui-product-approvals-common/types/ApprovalRules';
import { ApprovalStatus } from 'ui-product-approvals-common/types/ApprovalStatus';
import type { ApprovalActor } from 'ui-product-approvals-common/types/ApprovalActor';
import {
  serializeActor,
  type ActorKey,
} from 'ui-product-approvals-common/utils/actorKey';
import { ACTOR } from '../constants/actors';

export const hasPendingAgentApprovalStep = (steps: ApprovalStep[]): boolean =>
  steps.some(
    (step) =>
      step.approver.actor.actorType === ACTOR.AGENT &&
      step.approvalStepStatus === ApprovalStepStatus.APPROVAL_PENDING
  );

export const groupRowsByOrder = (
  approvalSteps: ApprovalStep[]
): ApprovalActor[][] => {
  const byOrder = new Map<number, ApprovalActor[]>();
  const withoutOrder: ApprovalActor[] = [];

  approvalSteps.forEach((step) => {
    if (step.order === undefined) {
      withoutOrder.push(step.approver);
      return;
    }
    const group = byOrder.get(step.order) ?? [];
    group.push(step.approver);
    byOrder.set(step.order, group);
  });

  const orderedGroups = [...byOrder.entries()]
    .sort(([orderA], [orderB]) => orderA - orderB)
    .map(([, group]) => group);

  return withoutOrder.length > 0
    ? [withoutOrder, ...orderedGroups]
    : orderedGroups;
};

export const groupStepsByOrder = <Step extends { order?: number }>(
  approvalSteps: Step[]
): Step[][] => {
  const byOrder = new Map<number, Step[]>();

  approvalSteps.forEach((step) => {
    const order = step.order ?? 0;
    const group = byOrder.get(order) ?? [];
    group.push(step);
    byOrder.set(order, group);
  });

  return [...byOrder.entries()]
    .sort(([orderA], [orderB]) => orderA - orderB)
    .map(([, group]) => group);
};

export const isGroupComplete = (
  group: ApprovalStep[],
  approvalRules: ApprovalRules
): boolean => {
  const isApproved = (step: ApprovalStep) =>
    step.approvalStepStatus === ApprovalStepStatus.APPROVED;

  return approvalRules === ApprovalRules.ANY_CAN_APPROVE
    ? group.some(isApproved)
    : group.every(isApproved);
};

// The status badge shown on a stage header in the redesigned Approvers section.
export enum StageStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  NOT_STARTED = 'NOT_STARTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

// Resolves the stage-header badge: a stage that has a rejection reads REJECTED,
// a satisfied stage reads APPROVED, the active (first-incomplete) stage reads
// IN_PROGRESS while the approval is still open, and later stages read
// NOT_STARTED. A changes-requested step on the active stage stays IN_PROGRESS.
export const getStageStatus = (
  group: ApprovalStep[],
  approvalRules: ApprovalRules,
  isActive: boolean
): StageStatus => {
  if (
    group.some(
      (step) => step.approvalStepStatus === ApprovalStepStatus.REJECTED
    )
  ) {
    return StageStatus.REJECTED;
  }
  if (isGroupComplete(group, approvalRules)) {
    return StageStatus.APPROVED;
  }
  return isActive ? StageStatus.IN_PROGRESS : StageStatus.NOT_STARTED;
};

// Approver display order within a step/stage by actor type: individual users
// first, then teams, then agents.
const ACTOR_TYPE_DISPLAY_RANK: Record<ActorType, number> = {
  [ACTOR.USER]: 0,
  [ACTOR.TEAM]: 1,
  [ACTOR.AGENT]: 2,
};

// A step is "completed" once it has been acted on (approved, rejected, or
// changes requested), as opposed to still-open pending/waiting steps.
const COMPLETED_STEP_STATUSES = new Set<ApprovalStepStatus>([
  ApprovalStepStatus.APPROVED,
  ApprovalStepStatus.REJECTED,
  ApprovalStepStatus.CHANGES_REQUESTED,
]);

// Returns a new array ordered by: (1) steps the viewer can act on (non-empty
// availableOperations), (2) completed/acted-on steps, (3) remaining
// pending/waiting steps. Within each bucket, sort by actor type (user, team,
// agent) then alphabetically by display name.
// `approvalStatus`/`approvalRules` are retained for call-site stability while the
// ordering is being iterated on; the ranking no longer depends on them.
export const sortStepsDecisiveFirst = (
  steps: ApprovalStep[],
  __approvalStatus: ApprovalStatus | undefined,
  __approvalRules: ApprovalRules
): ApprovalStep[] => {
  const actionableRank = (step: ApprovalStep): number =>
    (step.availableOperations?.length ?? 0) > 0 ? 0 : 1;

  const completedRank = (step: ApprovalStep): number =>
    COMPLETED_STEP_STATUSES.has(step.approvalStepStatus) ? 0 : 1;

  return [...steps].sort(
    (a, b) =>
      actionableRank(a) - actionableRank(b) ||
      completedRank(a) - completedRank(b) ||
      ACTOR_TYPE_DISPLAY_RANK[a.approver.actor.actorType] -
        ACTOR_TYPE_DISPLAY_RANK[b.approver.actor.actorType] ||
      a.approver.displayName.localeCompare(b.approver.displayName)
  );
};

export const isOrderedApproval = (steps: ApprovalStep[]): boolean =>
  groupStepsByOrder(steps).length > 1;

export const getStepGroupCount = (steps: ApprovalStep[]): number =>
  groupStepsByOrder(steps).length;

export const getCompletedStepGroupCount = (
  steps: ApprovalStep[],
  approvalRules: ApprovalRules
): number =>
  groupStepsByOrder(steps).filter((group) =>
    isGroupComplete(group, approvalRules)
  ).length;

export const getIncompleteStepGroupCount = (
  steps: ApprovalStep[],
  approvalRules: ApprovalRules
): number =>
  getStepGroupCount(steps) - getCompletedStepGroupCount(steps, approvalRules);

export const isStepApprovedOrRejected = (
  step: Pick<ApprovalStep, 'approvalStepStatus'>
): boolean =>
  step.approvalStepStatus === ApprovalStepStatus.APPROVED ||
  step.approvalStepStatus === ApprovalStepStatus.REJECTED;

export const shouldWarnInvalidApprover = (
  step: ApprovalStep,
  validApproverActorKeys: Set<ActorKey>,
  isFetchApproversError: boolean,
  isLoadingApprovers: boolean
): boolean =>
  !isFetchApproversError &&
  !isLoadingApprovers &&
  !isStepApprovedOrRejected(step) &&
  !validApproverActorKeys.has(serializeActor(step.approver.actor));

export const getPendingUserIds = (groups: ApprovalStep[][]): number[] => {
  const userIds = groups
    .flat()
    .filter(
      (step) =>
        step.approver.actor.actorType === ACTOR.USER &&
        !isStepApprovedOrRejected(step)
    )
    .map((step) => Number(step.approver.actor.id));
  return Array.from(new Set(userIds));
};

export const getUnapprovedUserIds = (
  stages: ApprovalStepDraft[],
  approvedActorKeys: Set<ActorKey>
): number[] => {
  const userIds = stages
    .flatMap((stage) => stage.approvers)
    .filter(
      ({ actor }) =>
        actor.actorType === ACTOR.USER &&
        !approvedActorKeys.has(serializeActor(actor))
    )
    .map(({ actor }) => Number(actor.id));
  return Array.from(new Set(userIds));
};

export const isAgentReviewingApprovalStep = (
  step: Pick<
    ApprovalStep,
    'approvalStepStatus' | 'approver' | 'inProgressTurnType'
  >
): boolean =>
  step.approvalStepStatus === ApprovalStepStatus.APPROVAL_PENDING &&
  step.approver.actor.actorType === ACTOR.AGENT &&
  step.inProgressTurnType === AgentTurnType.REVIEW;

export const toApprovalStepInputs = (
  steps: ApprovalStepDraft[]
): ApprovalStepInput[] =>
  steps
    .filter((step) => step.approvers.length > 0)
    .flatMap((step, index) =>
      step.approvers.map((approver) => ({
        actor: approver.actor,
        order: index,
      }))
    );

/**
 * Returns whether a mutation changes the serialized stage order of an actor
 * protected by an individual/approved lock. A stage-level lock freezes a stage's
 * contents but not its position, so it does not protect order here. Empty draft
 * stages are omitted by {@link toApprovalStepInputs}, so structural index checks
 * alone cannot catch mutations that make a preceding stage empty or non-empty.
 */
export const haveLockedSerializedOrdersChanged = (
  before: ApprovalStepDraft[],
  after: ApprovalStepDraft[],
  lockedActorKeys: Set<ActorKey>
): boolean => {
  const protectedActorKeys = lockedActorKeys;
  const beforeOrders = new Map<ActorKey, number | undefined>(
    toApprovalStepInputs(before)
      .filter((input) => protectedActorKeys.has(serializeActor(input.actor)))
      .map((input) => [serializeActor(input.actor), input.order])
  );
  const afterOrders = new Map<ActorKey, number | undefined>(
    toApprovalStepInputs(after).map((input) => [
      serializeActor(input.actor),
      input.order,
    ])
  );

  return [...beforeOrders].some(
    ([actorKey, order]) => afterOrders.get(actorKey) !== order
  );
};

export const existingStepsToInputs = (
  approvalSteps: ApprovalStep[]
): ApprovalStepInput[] =>
  approvalSteps.map((step) => ({
    actor: step.approver.actor,
    order: step.order,
  }));

export const countDistinctApprovers = (steps: ApprovalStepDraft[]): number => {
  const actorKeys = new Set<ActorKey>();
  steps.forEach((step) =>
    step.approvers.forEach((approver) =>
      actorKeys.add(serializeActor(approver.actor))
    )
  );
  return actorKeys.size;
};

export const countOrderedSteps = (steps: ApprovalStepDraft[]): number =>
  steps.filter((step) => step.approvers.length > 0).length;

export const getApproverTrackingCounts = (
  actors: Actor[]
): { userCount: number; teamCount: number; agentIds: string[] } => {
  const seenKeys = new Set<ActorKey>();
  const userKeys = new Set<ActorKey>();
  const teamKeys = new Set<ActorKey>();
  const agentIds = new Set<string>();
  actors.forEach((actor) => {
    const actorKey = serializeActor(actor);
    if (seenKeys.has(actorKey)) {
      return;
    }
    seenKeys.add(actorKey);
    if (actor.actorType === ACTOR.USER) {
      userKeys.add(actorKey);
    } else if (actor.actorType === ACTOR.TEAM) {
      teamKeys.add(actorKey);
    } else if (actor.actorType === ACTOR.AGENT) {
      agentIds.add(actor.id);
    }
  });
  return {
    userCount: userKeys.size,
    teamCount: teamKeys.size,
    agentIds: [...agentIds],
  };
};

export const hasEmptyStep = (steps: ApprovalStepDraft[]): boolean =>
  steps.some((step) => step.approvers.length === 0);

export const hasHumanApprover = (steps: ApprovalStepDraft[]): boolean =>
  steps.some((step) =>
    step.approvers.some(
      (approver) =>
        approver.actor.actorType === ACTOR.USER ||
        approver.actor.actorType === ACTOR.TEAM
    )
  );

export const stepHasLockedApprover = (
  step: ApprovalStepDraft,
  lockedActorKeys: Set<ActorKey>
): boolean =>
  step.approvers.some((approver) =>
    lockedActorKeys.has(serializeActor(approver.actor))
  );

export const isStepGroupComplete = (
  step: ApprovalStepDraft,
  approvedActorKeys: Set<ActorKey>,
  approvalRules: ApprovalRules
): boolean => {
  if (step.approvers.length === 0) {
    return false;
  }
  const isApproved = (approver: ApprovalActor) =>
    approvedActorKeys.has(serializeActor(approver.actor));
  return approvalRules === ApprovalRules.ANY_CAN_APPROVE
    ? step.approvers.some(isApproved)
    : step.approvers.every(isApproved);
};

export const stepOrderIsLocked = (
  step: ApprovalStepDraft,
  lockedActorKeys: Set<ActorKey>
): boolean => stepHasLockedApprover(step, lockedActorKeys);

export const addingStepChangesLockedOrder = (
  steps: ApprovalStepDraft[],
  atIndex: number,
  lockedActorKeys: Set<ActorKey>
): boolean =>
  steps
    .slice(Math.min(Math.max(atIndex, 0), steps.length))
    .some((step) => stepOrderIsLocked(step, lockedActorKeys));

export const removingStepChangesLockedOrder = (
  steps: ApprovalStepDraft[],
  atIndex: number,
  lockedActorKeys: Set<ActorKey>
): boolean =>
  atIndex >= 0 &&
  steps
    .slice(atIndex + 1)
    .some((step) => stepOrderIsLocked(step, lockedActorKeys));

export const approvalStepsSignature = (steps: ApprovalStepDraft[]): string =>
  steps
    .filter((step) => step.approvers.length > 0)
    .map((step) =>
      step.approvers
        .map((approver) => serializeActor(approver.actor))
        .sort()
        .join(',')
    )
    .join('|');
